import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';

const FadeUp = ({ children, delay = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }} transition={{ duration: 0.6, ease: 'easeOut', delay }}>
    {children}
  </motion.div>
);

const ADVERTISER_TARGETS = [
  { emoji: '🎬', label: 'Casting' },
  { emoji: '🎓', label: 'Écoles de mode' },
  { emoji: '💪', label: 'Fitness & Sport' },
  { emoji: '💄', label: 'Beauté & Soins' },
  { emoji: '🦷', label: 'Esthétique' },
  { emoji: '👗', label: 'Mode & Stylisme' },
  { emoji: '🏆', label: 'Sport Pro' },
  { emoji: '📱', label: 'Apps & Digital' },
];

export default function Pricing() {
  const [siteLang, setSiteLang] = useState('FR');
  const navigate = useNavigate();

  const content = {
    FR: {
      title: 'TARIFS',
      sub: 'Simple, honnête, transparent.',
      freeTitle: 'COMPIFY EST 100% GRATUIT',
      freeSub: 'Financé par la publicité ciblée. Aucune carte bancaire, aucun abonnement, jamais.',
      freeArgs: [
        { icon: '∞', title: 'Gratuit pour toujours', desc: 'Pas de freemium caché, pas de limite cachée. Créez autant de composites que vous voulez.' },
        { icon: '⊘', title: 'Sans inscription', desc: 'Créez votre composite immédiatement, sans créer de compte.' },
        { icon: '🔒', title: 'Photos protégées', desc: 'Vos photos ne quittent jamais votre appareil. Aucun stockage sur nos serveurs.' },
        { icon: '✦', title: 'Tous les templates', desc: '8 templates recto + 8 templates verso disponibles gratuitement.' },
      ],
      agenceTitle: 'PLAN AGENCE',
      agencePrice: '49,99€',
      agencePeriod: '/mois',
      agenceDesc: 'Pour les agences et professionnels qui gèrent plusieurs mannequins.',
      agenceFeatures: [
        'Multi-comptes mannequins',
        'Logo agence personnalisé dans le composite',
        'Sans publicité',
        'Templates exclusifs',
        'Export illimité',
        'Support dédié prioritaire',
      ],
      agenceCta: 'NOUS CONTACTER',
      advertTitle: 'VOUS ÊTES ANNONCEUR ?',
      advertSub: 'Touchez directement une audience de mannequins et aspirants mannequins — ciblée, qualifiée, passionnée.',
      advertCta: 'DEVENIR ANNONCEUR',
      cta: 'CRÉER MON COMPOSITE GRATUITEMENT',
    },
    EN: {
      title: 'PRICING',
      sub: 'Simple, honest, transparent.',
      freeTitle: 'COMPIFY IS 100% FREE',
      freeSub: 'Funded by targeted advertising. No credit card, no subscription, ever.',
      freeArgs: [
        { icon: '∞', title: 'Free forever', desc: 'No hidden freemium, no hidden limits. Create as many composites as you want.' },
        { icon: '⊘', title: 'No sign up', desc: 'Create your composite immediately, no account needed.' },
        { icon: '🔒', title: 'Photos protected', desc: 'Your photos never leave your device. Nothing stored on our servers.' },
        { icon: '✦', title: 'All templates', desc: '8 front templates + 8 back templates, all free.' },
      ],
      agenceTitle: 'AGENCY PLAN',
      agencePrice: '€49.99',
      agencePeriod: '/month',
      agenceDesc: 'For agencies and professionals managing multiple models.',
      agenceFeatures: [
        'Multiple model accounts',
        'Custom agency logo in composite',
        'Ad-free',
        'Exclusive templates',
        'Unlimited exports',
        'Dedicated priority support',
      ],
      agenceCta: 'CONTACT US',
      advertTitle: 'ARE YOU AN ADVERTISER?',
      advertSub: 'Reach a targeted audience of models and aspiring models — qualified and passionate.',
      advertCta: 'BECOME AN ADVERTISER',
      cta: 'CREATE MY COMPOSITE FOR FREE',
    }
  };

  const d = content[siteLang];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff', fontFamily: 'Arial, sans-serif', display: 'flex', flexDirection: 'column' }}>
      <Header siteLang={siteLang} setSiteLang={setSiteLang} />

      {/* HERO */}
      <section style={{ backgroundColor: '#0e0e0e', padding: '80px 24px', textAlign: 'center' }}>
        <FadeUp>
          <p style={{ color: '#FF0066', fontSize: '11px', letterSpacing: '5px', margin: '0 0 16px', fontWeight: '700' }}>COMPIFY</p>
          <h1 style={{ color: '#fff', fontSize: '42px', fontWeight: '900', letterSpacing: '2px', margin: '0 0 20px', textTransform: 'uppercase', lineHeight: 1.1 }}>{d.title}</h1>
          <p style={{ color: '#888', fontSize: '16px', margin: 0 }}>{d.sub}</p>
        </FadeUp>
      </section>

      {/* 100% GRATUIT */}
      <section style={{ backgroundColor: '#f7f7f7', padding: '100px 24px' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          <FadeUp>
            <div style={{ textAlign: 'center', marginBottom: '64px' }}>
              <h2 style={{ color: '#1A1A1A', fontSize: '32px', fontWeight: '900', margin: '0 0 16px', letterSpacing: '2px' }}>{d.freeTitle}</h2>
              <div style={{ width: '40px', height: '2px', backgroundColor: '#FF0066', margin: '0 auto 20px' }} />
              <p style={{ color: '#888', fontSize: '15px', margin: 0, lineHeight: 1.7 }}>{d.freeSub}</p>
            </div>
          </FadeUp>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }}
            style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1px', backgroundColor: '#e0e0e0' }}>
            {d.freeArgs.map((arg, i) => (
              <motion.div key={i}
                variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
                whileHover={{ backgroundColor: '#fff', boxShadow: '0 10px 40px rgba(0,0,0,0.06)' }}
                style={{ backgroundColor: '#fff', padding: '40px 28px', textAlign: 'center', transition: 'all 0.3s' }}>
                <p style={{ fontSize: '32px', margin: '0 0 16px' }}>{arg.icon}</p>
                <p style={{ color: '#1A1A1A', fontSize: '13px', fontWeight: '700', margin: '0 0 10px', letterSpacing: '1.5px', textTransform: 'uppercase' }}>{arg.title}</p>
                <p style={{ color: '#888', fontSize: '13px', lineHeight: 1.7, margin: 0 }}>{arg.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* PLAN AGENCE */}
      <section style={{ backgroundColor: '#0e0e0e', padding: '100px 24px' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <FadeUp>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px', alignItems: 'center' }}>
              <div>
                <p style={{ color: '#FF0066', fontSize: '11px', letterSpacing: '4px', fontWeight: '700', margin: '0 0 16px' }}>{d.agenceTitle}</p>
                <p style={{ color: '#fff', fontSize: '52px', fontWeight: '900', margin: '0 0 4px', lineHeight: 1 }}>
                  {d.agencePrice}<span style={{ fontSize: '16px', opacity: 0.5, fontWeight: '400' }}>{d.agencePeriod}</span>
                </p>
                <div style={{ width: '40px', height: '2px', backgroundColor: '#FF0066', margin: '20px 0' }} />
                <p style={{ color: '#888', fontSize: '13px', lineHeight: 1.7, margin: '0 0 32px' }}>{d.agenceDesc}</p>
                <motion.button onClick={() => navigate('/contact')}
                  whileHover={{ scale: 1.03, backgroundColor: '#ff1a75' }} whileTap={{ scale: 0.97 }}
                  style={{ backgroundColor: '#FF0066', color: '#fff', border: 'none', padding: '14px 32px', fontSize: '11px', letterSpacing: '2px', fontWeight: '700', cursor: 'pointer' }}>
                  {d.agenceCta}
                </motion.button>
              </div>
              <div style={{ borderLeft: '1px solid #2a2a2a', paddingLeft: '48px' }}>
                {d.agenceFeatures.map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0', borderBottom: i < d.agenceFeatures.length - 1 ? '1px solid #1a1a1a' : 'none' }}>
                    <span style={{ color: '#FF0066', fontWeight: '700', flexShrink: 0 }}>✓</span>
                    <span style={{ color: '#aaa', fontSize: '13px' }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ANNONCEURS */}
      <section style={{ backgroundColor: '#fff', padding: '100px 24px' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          <FadeUp>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '48px' }}>
              <div>
                <p style={{ color: '#FF0066', fontSize: '11px', letterSpacing: '4px', fontWeight: '700', margin: '0 0 12px' }}>{d.advertTitle}</p>
                <p style={{ color: '#888', fontSize: '14px', lineHeight: 1.7, margin: 0, maxWidth: '500px' }}>{d.advertSub}</p>
              </div>
              <motion.button onClick={() => navigate('/contact')}
                whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                style={{ backgroundColor: '#FF0066', color: '#fff', border: 'none', padding: '14px 32px', fontSize: '11px', letterSpacing: '2px', fontWeight: '700', cursor: 'pointer', flexShrink: 0, marginLeft: '32px' }}>
                {d.advertCta}
              </motion.button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: '8px' }}>
              {ADVERTISER_TARGETS.map((target, i) => (
                <motion.div key={i} whileHover={{ scale: 1.05, borderColor: '#FF0066' }}
                  style={{ border: '1px solid #eee', padding: '16px 8px', textAlign: 'center', transition: 'border-color 0.2s' }}>
                  <p style={{ fontSize: '22px', margin: '0 0 8px' }}>{target.emoji}</p>
                  <p style={{ color: '#888', fontSize: '9px', letterSpacing: '1px', margin: 0, fontWeight: '700' }}>{target.label}</p>
                </motion.div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* CTA */}
      <section style={{ backgroundColor: '#FF0066', padding: '80px 24px', textAlign: 'center' }}>
        <FadeUp>
          <motion.button onClick={() => navigate('/editeur')}
            whileHover={{ scale: 1.04, backgroundColor: '#1A1A1A' }} whileTap={{ scale: 0.97 }}
            style={{ backgroundColor: '#fff', color: '#FF0066', border: 'none', padding: '18px 48px', fontSize: '13px', letterSpacing: '3px', fontWeight: '900', cursor: 'pointer' }}>
            {d.cta}
          </motion.button>
        </FadeUp>
      </section>

      <Footer siteLang={siteLang} />
    </div>
  );
}